<script setup lang="ts">
import { FileInterface } from '@/types';
import { EMIT } from '@/enum';
import { computed, onMounted, onBeforeUnmount, toRefs, ref, watch } from 'vue';
import { useMessageCenterStore } from '@/store';
import { storeToRefs } from 'pinia';
import AudioMessage from '@/components/MessageCenter/components/Chat/components/AudioMessage.vue';
import MessageActions from '@/components/MessageCenter/components/Chat/components/MessageActions';
import { mimeFromExt } from '@/composables';

interface Props {
  file: FileInterface;
  tag?: string;
  messageId?: number;
  createdAt: Date | string;
  documentId?: number;
  isYou?: boolean;
  previewFile?: boolean;
  metadata?: string;
}

const props = withDefaults(defineProps<Props>(), {
  tag: 'li',
  previewFile: false,
  messageId: undefined,
  documentId: undefined,
  metadata: undefined,
});

const emit = defineEmits<{
  (eventName: EMIT.REMOVE, file: FileInterface): void;
  (eventName: EMIT.SHOW_PREVIEW, file: Blob | File): void;
}>();

const { createdAt, file, previewFile, tag, documentId, messageId, isYou } = toRefs(props);
const messageCenterStore = useMessageCenterStore();
const { attachmentFile } = storeToRefs(messageCenterStore);
const { getFileTypes } = toRefs(messageCenterStore);

const blobUrl = ref<string | null>(null);
const isLocalFile = ref<boolean>(false);

const fileTypeIcon = computed(() => {
  let ext = '';
  if (file.value?.file && (file.value.file as Blob).type) {
    const mime = (file.value.file as Blob).type;
    const mimeParts = mime.split('/');
    ext = mimeParts[mimeParts.length - 1];
    ext = (file.value.file as File)?.name?.split('.').pop()?.toLowerCase() || ext;
  } else if (file.value?.name) {
    ext = file.value.name.split('.').pop()?.toLowerCase() || '';
  }

  const iconType = getFileTypes.value.find((type) => type.ext.toLowerCase() === ext);
  return iconType ? iconType.icon : 'insert_drive_file';
});

const fileSize = computed(() => {
  if (!file.value || !file.value.file) return '0.00 MB';
  return ((file.value.file as Blob).size / 1024 / 1024).toFixed(2) + ' MB';
});

const isAudioFile = computed(() => {
  const audioExtensions = getFileTypes.value.filter((type) => type.category === 'audio').map((type) => type.ext);
  const extension = file.value.name.split('.').pop()?.toLowerCase();
  return audioExtensions.includes(extension || '');
});

const guessAudioMime = (name?: string) => {
  const ext = name?.split('.').pop()?.toLowerCase();
  return mimeFromExt(ext).mime;
};

const toBlob = (input: Blob | ArrayBuffer, typeHint?: string) => {
  if (input instanceof Blob) return input;

  if (input instanceof ArrayBuffer) return new Blob([new Uint8Array(input)], { type: typeHint });
  if (input?.buffer && input?.byteLength !== undefined) return new Blob([input], { type: typeHint });

  return new Blob([input], { type: typeHint || 'application/octet-stream' });
};

const setBlobUrlFrom = (data: Blob | ArrayBuffer, mime?: string) => {
  const next = toBlob(data, mime);
  const oldUrl = blobUrl.value;
  blobUrl.value = URL.createObjectURL(next);
  if (oldUrl) URL.revokeObjectURL(oldUrl);
};

const loadAndPreview = async (local?: Blob | File) => {
  if (local && !isAudioFile.value) {
    isLocalFile.value = true;
    emit(EMIT.SHOW_PREVIEW, local);
    return;
  }

  if (messageId.value) await messageCenterStore.loadMessageFile(messageId.value, file.value);

  if (attachmentFile.value && !isAudioFile.value) {
    const type = attachmentFile.value.type || 'application/octet-stream';
    const blob = new Blob([attachmentFile.value.content], { type });
    emit(EMIT.SHOW_PREVIEW, blob);
    return;
  }

  if (isAudioFile.value) {
    isLocalFile.value = true;
    const mime = (local as Blob)?.type || attachmentFile.value?.type || guessAudioMime(file.value?.name);
    const content = local ?? attachmentFile.value?.content;
    if (content) setBlobUrlFrom(content, mime);
  }
};

onMounted(() => {
  if (blobUrl.value) blobUrl.value = null;
});

onBeforeUnmount(() => {
  if (blobUrl.value) URL.revokeObjectURL(blobUrl.value);
});
const onClickPreview = () => {
  blobUrl.value = null;
  if (file.value?.file instanceof Blob) {
    isLocalFile.value = true;
    loadAndPreview(file.value.file);
  } else {
    loadAndPreview();
  }
};

watch(
  () => [file.value?.file, attachmentFile.value],
  (f) => {
    if (attachmentFile.value === undefined) {
      blobUrl.value = null;
      return;
    }
    if (isAudioFile.value && f instanceof Blob) {
      const mime = f.type || guessAudioMime(file.value?.name);
      const audioBlob = toBlob(f, mime);
      blobUrl.value = null;
      blobUrl.value = URL.createObjectURL(audioBlob);
    }
  },
  { immediate: true },
);
</script>

<template>
  <component :is="tag" class="file-preview relative" :style="isYou ? 'padding-top: 1rem;' : ''">
    <div :class="[previewFile ? 'col-10' : 'col-6', 'flex', 'align-items-center']">
      <div v-if="!isAudioFile" class="box-preview" @click="onClickPreview()">
        <span class="material-icons drive mx-1" v-text="fileTypeIcon" />
        <span class="title font-light my-0 mx-1 cursor-pointer" v-text="file.name" />
      </div>
      <AudioMessage
        v-if="isAudioFile"
        :key="file.name"
        :src="blobUrl"
        :is-for-preview="true"
        :meta-duration="metadata ? JSON.parse(metadata)?.duration : null"
        @play="onClickPreview"
      />
    </div>

    <template v-if="!previewFile">
      <div class="col-2">
        <span class="text-xs text-gray-500 mx-1 flex items-center">({{ fileSize }})</span>
      </div>

      <div class="close absolute right-0 my-0 mx-1 cursor-pointer" @click="emit(EMIT.REMOVE, file)">
        <span class="material-icons">delete</span>
      </div>
    </template>

    <template v-else-if="isYou">
      <div class="file-actions">
        <MessageActions
          :created-at="createdAt"
          :message-id="messageId"
          :document-id="documentId"
          :action="EMIT.DELETE_MESSAGE_FILE"
        />
      </div>
    </template>
  </component>
</template>

<style scoped>
.file-actions {
  position: absolute;
  top: 1rem;
  right: 0.25rem;
  z-index: 1;
}
</style>

<style scoped lang="scss">
.file-preview {
  color: $grayDark;
  font-size: 14px;
  line-height: 18px;

  .title {
    color: $grayDark;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.box-preview {
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
}

.box-preview:hover {
  color: var(--primary-color);
  span {
    color: var(--primary-color);
  }
}
</style>
